import React, { useEffect, useState } from "react";
import { CeoNav } from "../components/CeoNav";
import { StatCard } from "../components/StatCard";
import { api, fmtNGN } from "../lib/api";
import { CardSkeleton, EmptyState, ErrorState } from "../components/LoadState";
import { ClipboardList, Package, Coins, UsersRound, BookOpen, Banknote, UserCog, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  AreaChart, Area,
} from "recharts";

export default function CeoDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/dashboard/ceo");
      setData(data);
    } catch (e) {
      setError(e.userMessage || "Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ceo-dashboard">
      <CeoNav />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold">Operations Overview</h1>
          <div className="text-sm text-muted-foreground mt-1">{today}</div>
        </div>

        {error ? <div className="mb-6"><ErrorState message={error} onRetry={load} /></div> : null}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            testId="ceo-stat-jobs"
            label="Jobs Today"
            value={data?.jobs_today ?? "—"}
            icon={<ClipboardList size={18} />}
            sub="Active entries recorded"
          />
          <StatCard
            testId="ceo-stat-crates"
            label="Total Crates"
            value={data?.crates_today ?? "—"}
            icon={<Package size={18} />}
            sub="Processed today"
          />
          <StatCard
            testId="ceo-stat-earnings"
            label="Total Earnings"
            value={data ? fmtNGN(data.earnings_today) : "—"}
            icon={<Coins size={18} />}
            accent
            sub="Payable to staff today"
          />
          <StatCard
            testId="ceo-stat-staff"
            label="Active Staff"
            value={data?.active_staff ?? "—"}
            icon={<UsersRound size={18} />}
            sub="In roster"
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <CardSkeleton rows={2} />
            <CardSkeleton rows={2} />
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="card-soft p-5" data-testid="chart-daily">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <ClipboardList size={14} />
              </div>
              <div className="font-display font-semibold">Daily Job Volume</div>
            </div>
            <div className="h-64">
              {(data?.daily_jobs || []).length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.daily_jobs}>
                    <CartesianGrid stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                    <YAxis tickLine={false} axisLine={false} fontSize={12} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="jobs" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState title="No daily activity" message="Jobs will appear here once entries are recorded." />
              )}
            </div>
          </div>

          <div className="card-soft p-5" data-testid="chart-monthly">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <TrendingUp size={14} />
              </div>
              <div className="font-display font-semibold">Monthly Earnings Trend</div>
            </div>
            <div className="h-64">
              {(data?.monthly_earnings || []).length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.monthly_earnings}>
                    <defs>
                      <linearGradient id="gEarn" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                    <YAxis tickLine={false} axisLine={false} fontSize={12} tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmtNGN(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="earnings" stroke="hsl(var(--success))" strokeWidth={2} fill="url(#gEarn)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState title="No earnings yet" message="Monthly totals will appear after entries are saved." />
              )}
            </div>
          </div>
        </div>
        )}

        <div>
          <div className="font-display font-semibold mb-3">Quick Actions</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <QuickLink to="/ceo/ledger" icon={<BookOpen size={16} />} title="View Ledger" sub="See all recorded entries" color="primary" testId="qa-ledger" />
            <QuickLink to="/ceo/payroll" icon={<Banknote size={16} />} title="Generate Payroll" sub="Monthly staff payments" color="emerald" testId="qa-payroll" />
            <QuickLink to="/ceo/payroll" icon={<UserCog size={16} />} title="Staff Earnings" sub="Individual breakdown" color="amber" testId="qa-staff" />
          </div>
        </div>
      </main>
    </div>
  );
}

function QuickLink({ to, icon, title, sub, color, testId }) {
  const palette = {
    primary: "bg-primary/10 text-primary",
    emerald: "bg-emerald-100 text-emerald-600",
    amber: "bg-amber-100 text-amber-600",
  }[color];
  return (
    <Link to={to} data-testid={testId} className="card-soft p-4 flex items-start gap-3 hover:-translate-y-0.5 hover:shadow-md transition-all">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${palette}`}>{icon}</div>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
      </div>
    </Link>
  );
}
